import { Box, useTheme } from '@mui/material';
import { BarChart, barElementClasses } from '@mui/x-charts';
import { DatasetType } from '@mui/x-charts/internals';
import { BlueberrySizeDataModel } from '../types';
import {
  decimalToRoundedPercentageString,
  getBucketTotal,
} from '../utils/getBucketTotal';
import { BlueberrySizeChartSegmentedBar } from './BlueberrySizeBar';

const formatCount = (val: number | null) => {
  if (val === null) {
    return '';
  }
  return `${Math.round(val).toLocaleString()}`;
};

/**
 * Stacked bar that displays a tooltip on hover
 * Due to MUI chart styling limitations, this is used to render a single bar at a time,
 * using the BarChart component (with axes and bars hidden) for the tooltip and a custom
 * component to display the data bar underneath.
 * @returns
 */
export const BlueberrySizeChartBarWithHover = ({
  data,
  fullBarValue = 0,
}: {
  data: BlueberrySizeDataModel;
  fullBarValue: number;
}) => {
  const theme = useTheme();

  const formatPercentage = (val: number | null) => {
    if (val === null) {
      return '';
    }
    return decimalToRoundedPercentageString(val / getBucketTotal(data), 1);
  };

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: '16px',
        display: 'grid',
        gridTemplateAreas: `'overlap'`,
        alignItems: 'center',
        // style is set here so both the MUI chart and our custom bar
        // can respond to hover
        '&:hover .fadeOnHover': { opacity: 0.5 },
      }}
    >
      <Box gridArea={'overlap'} zIndex={1} height={'100%'}>
        <BarChart
          layout='horizontal'
          dataset={[data] as DatasetType}
          skipAnimation
          hideLegend
          height={16}
          series={[
            {
              dataKey: 'green',
              stack: '1',
              id: 'green',
              // values below are for the tooltip:
              color: theme.colors.canopy.components.barChart.green,
              label: formatCount(data.green),
              valueFormatter: formatPercentage,
            },
            {
              dataKey: 'yellow',
              stack: '1',
              id: 'pink',
              // values below are for the tooltip:
              color: theme.colors.canopy.components.barChart.pink,
              label: formatCount(data.yellow),
              valueFormatter: formatPercentage,
            },
            {
              dataKey: 'purple',
              stack: '1',
              id: 'purple',
              // values below are for the tooltip:
              color: theme.colors.canopy.components.barChart.purple,
              label: formatCount(data.purple),
              valueFormatter: formatPercentage,
            },
            {
              dataKey: 'blue',
              stack: '1',
              id: 'blue',
              // values below are for the tooltip:
              color: theme.colors.canopy.components.barChart.blue,
              label: formatCount(data.blue),
              valueFormatter: formatPercentage,
            },
          ]}
          yAxis={[
            {
              scaleType: 'band',
              // this actually hides the tooltip title, not whole the tooltip
              hideTooltip: true,
              // still needs some string to display tooltip
              data: [''],
              position: 'none',
            },
          ]}
          xAxis={[{ position: 'none' }]}
          // FYI: 0 left margin sometimes causes the leftmost bars to be hidden
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          // Hide hover highlighting, chart axes, and MUI bars
          axisHighlight={{ y: 'none' }}
          sx={{
            [`.${barElementClasses.root}`]: {
              display: 'none',
            },
          }}
        />
      </Box>
      <Box gridArea={'overlap'}>
        <BlueberrySizeChartSegmentedBar
          data={data}
          fullBarValue={fullBarValue || getBucketTotal(data)}
        />
      </Box>
    </Box>
  );
};
